import { useRef, useState } from "react";
import { useStore } from "zustand";

import type { ComposerThreadTarget } from "../../composerDraftStore";
import type { DraftId } from "../../draftId";
import { promptStashStore, type PromptStashEntry } from "../../promptStashStore";
import { createComposerStashActions } from "./composerStashActions";

type ComposerStashTarget = ComposerThreadTarget | DraftId;
export type ComposerStashNoticeType = "error" | "success" | "warning";

export interface ComposerStashController {
  readonly hasEntries: boolean;
  readonly save: () => void;
  readonly restore: (entry: PromptStashEntry) => void;
}

type ComposerStashSaveResult = Awaited<
  ReturnType<ReturnType<typeof createComposerStashActions>["save"]>
>;
type ComposerStashSaveNoticeStatus = Exclude<ComposerStashSaveResult["status"], "saved">;
type ComposerStashNotice = {
  readonly message: string;
  readonly type: ComposerStashNoticeType;
};

interface UseComposerStashControllerInput {
  readonly target: ComposerStashTarget;
  readonly onNotice: (message: string, type: ComposerStashNoticeType) => void;
  readonly onSaved?: (() => void) | undefined;
  readonly onRestored?: (() => void) | undefined;
  readonly onEmpty?: (() => void) | undefined;
}

function useComposerStashController(
  input: UseComposerStashControllerInput,
): ComposerStashController {
  const actions = useRef(createComposerStashActions(promptStashStore)).current;
  const hasEntries = useStore(promptStashStore, (state) => state.entries.length > 0);

  const save = () => {
    void actions.save(input.target).then((result) => {
      if (result.status === "empty" && input.onEmpty) {
        input.onEmpty();
        return;
      }
      const notice = saveNotice(result);
      input.onNotice(notice.message, notice.type);
      if (result.status === "saved") input.onSaved?.();
    });
  };

  const restore = (entry: PromptStashEntry) => {
    void Promise.resolve(actions.restore(input.target, entry)).then((result) => {
      const notice = restoreNotice(result);
      input.onNotice(notice.message, notice.type);
      if (result.status === "restored") input.onRestored?.();
    });
  };

  return { hasEntries, save, restore };
}

export function useComposerStashMenu(input: Omit<UseComposerStashControllerInput, "onEmpty">) {
  const [open, setOpen] = useState(false);
  const controller = useComposerStashController({ ...input, onEmpty: () => setOpen(true) });
  return { controller, menu: { open, onOpenChange: setOpen } };
}

function saveNotice(result: ComposerStashSaveResult): ComposerStashNotice {
  if (result.status === "saved") return savedNotice(result.evicted);
  return SAVE_NOTICES[result.status];
}

const SAVE_NOTICES: Record<ComposerStashSaveNoticeStatus, ComposerStashNotice> = {
  empty: { message: "There is nothing to stash", type: "warning" },
  storage: { message: "Could not save the prompt stash", type: "error" },
  changed: { message: "The prompt changed before it was saved", type: "warning" },
  images: { message: "Could not save every image", type: "error" },
  files: { message: "Could not save every file", type: "error" },
  busy: { message: "Prompt stash is already saving", type: "warning" },
  staged: { message: "Wait for the current send to finish", type: "warning" },
};

function savedNotice(evicted: PromptStashEntry | null | undefined) {
  return evicted
    ? { message: "Prompt stashed. The oldest saved prompt was removed.", type: "success" as const }
    : { message: "Prompt stashed", type: "success" as const };
}

function restoreNotice(
  result: Awaited<ReturnType<ReturnType<typeof createComposerStashActions>["restore"]>>,
): {
  readonly message: string;
  readonly type: ComposerStashNoticeType;
} {
  switch (result.status) {
    case "restored":
      return { message: "Prompt restored", type: "success" };
    case "retained":
      return {
        message: "Could not remove the stashed prompt. The restored draft was rolled back.",
        type: "warning",
      };
    case "unavailable":
      return { message: "That stashed prompt is no longer available.", type: "warning" };
    case "destination":
      return { message: "Could not restore the stashed prompt", type: "warning" };
  }
}
